import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { find, filter, reduce } from 'lodash';
import { connect } from 'react-redux';
import Toggle from 'react-bootstrap-toggle';
import AccountNameRenderer from '../../../../components/table-cells/AccountNameRenderer';
import OpportunityNameRenderer from '../../../../components/table-cells/OpportunityNameRenderer';
import LogRenderer from '../../../../components/table-cells/LogRenderer';
import AgeRenderer from '../../../../components/table-cells/AgeRenderer';
import ArrayRenderer from '../../../../components/table-cells/ArrayRenderer';
import DateRenderer from '../../../../components/table-cells/DateRenderer';
import { getUser } from '../../../Auth/AuthReducer';
import { getTablePreference } from '../../../App/AppReducer';
import SaleRepsNameRenderer from '../../../../components/table-cells/SaleRepsNameRenderer';

import {
  dateComparator, stringComparator, minDateAggregation,
} from '../../../../components/table-cells/helpers';
import GodTable, { CUSTOM_FIELD_TYPES } from '../../../../components/god-table';

class OpportunityTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      groupingByCloseDate: false,
      closeDateGroupingType: 'monthly',
      showAllDeals: true,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.hanldeOnColumnRowGroupChanged = this.hanldeOnColumnRowGroupChanged.bind(this);
    this.getMainMenuItems = this.getMainMenuItems.bind(this);
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    params.api.addAggFunc('minDate', minDateAggregation);
  }

  getMainMenuItems(params) {
    const { groupingByCloseDate, closeDateGroupingType } = this.state;
    const columnId = params.column.getId();
    if (groupingByCloseDate && columnId === 'ag-Grid-AutoColumn') {
      const menuItems = params.defaultItems.slice(0);
      const subMenus = [
        { name: 'Monthly', value: 'monthly' },
        { name: 'Quarterly', value: 'quarterly' },
        { name: 'Yearly', value: 'yearly' },
      ];
      menuItems.push({
        name: 'Group By Close Date',
        checked: groupingByCloseDate,
        subMenu: subMenus.map(item => ({
          ...item,
          checked: item.value === closeDateGroupingType,
          action: () => {
            this.setState(
              { closeDateGroupingType: item.value },
              () => {
                if (this.gridApi) {
                  this.gridApi.refreshClientSideRowModel('group');
                }
              },
            );
          },
        })),
      });
      return menuItems;
    }
    return params.defaultItems;
  }

  getCloseDateColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'close_date',
      sort: 'desc',
      type: 'dateColumn',
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'logRenderer',
          params: {
            field: 'close_date',
          },
        };
      },
      enableRowGroup: true,
      cellRendererParams: {
        isCloseDate: true,
        column,
      },
      valueGetter: (params) => {
        if (params && params.data) {
          return moment(params.data.close_date).format('YYYY-MM-DD');
        }
        return null;
      },
      keyCreator: (params) => {
        const vals = params.value;
        if (!vals) {
          return '--';
        }
        let format = 'Y \\QQ';
        if (this.state.closeDateGroupingType === 'quarterly') {
          format = 'Y \\QQ';
        } else if (this.state.closeDateGroupingType === 'monthly') {
          format = 'Y MM';
        } else if (this.state.closeDateGroupingType === 'yearly') {
          format = 'Y';
        }
        return moment(vals, 'YYYY-MM-DD').format(format);
      },
      valueFormatter: (params) => {
        if (params.value) {
          let format = 'Y \\QQ';
          let displayFormat = '\\QQ Y';
          if (this.state.closeDateGroupingType === 'quarterly') {
            format = 'Y \\QQ';
            displayFormat = '\\QQ Y';
          } else if (this.state.closeDateGroupingType === 'monthly') {
            format = 'Y MM';
            displayFormat = 'MMMM Y';
          } else if (this.state.closeDateGroupingType === 'yearly') {
            format = 'Y';
            displayFormat = 'Y';
          }
          return moment(`${params.value}`, format).format(displayFormat);
        }
        return params.value;
      },
    };
  }

  getAccountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      rowDrag: true,
      enablePivot: true,
      enableRowGroup: true,
      field: 'account.name',
      comparator: stringComparator,
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'accountNameRenderer',
          params: {
            enableAutoHeight: true,
            column,
          },
        };
      },
    };
  }

  getNameColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'name',
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      comparator: stringComparator,
      cellRenderer: 'opportunityNameRenderer',
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
    };
  }

  getOwnerColumn(column, isAccountOwner = false) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      rowDrag: true,
      enablePivot: true,
      enableRowGroup: true,
      field: isAccountOwner ? 'account.owner.name' : 'owner.name',
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'saleRepsNameRenderer',
          params: {
            enableAutoHeight: true,
            column,
            isAccountOwner,
          },
        };
      },
    };
  }

  getAmountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'amount',
      type: 'numericColumn',
      cellRenderer: 'logRenderer',
      cellRendererParams: {
        field: 'amount',
        column,
      },
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }

  getStageColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'stage_name',
      enablePivot: true,
      enableRowGroup: true,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'logRenderer',
          params: {
            field: 'stage_name',
          },
        };
      },
    };
  }

  getAgeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'pursuit_length',
      cellRenderer: 'ageRenderer',
      type: 'numericColumn',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getProductColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'products',
      cellRenderer: 'arrayRenderer',
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
    };
  }

  getTypeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'types',
      enableRowGroup: true,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
      keyCreator: (params) => {
        const vals = params.value;
        if (!vals || vals.length === 0) {
          return '--';
        }
        return params.value.sort().join(', ');
      },
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'arrayRenderer',
          params: {
            field: 'types',
          },
        };
      },
    };
  }

  getIndustryColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'account.industry_names',
      enableRowGroup: true,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
      keyCreator: (params) => {
        const vals = params.value;
        if (!vals || vals.length === 0) {
          return '--';
        }
        return params.value.sort().join(', ');
      },
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'arrayRenderer',
          params: {
            field: 'account.industry_names',
          },
        };
      },
    };
  }

  getLastActivityColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'last_activity_date',
      type: 'dateColumn',
      cellRenderer: 'dateRenderer',
      cellRendererParams: {
        format: 'short',
        column,
      },
    };
  }

  getRequestedFeedbackColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: column.target_field,
      cellRenderer: 'requestedFeedbackRenderer',
      cellRendererParams: {
        column,
      },
    };
  }

  getContactCountColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'contact_count',
      type: 'numericColumn',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getColDefs() {
    const { tableColumns } = this.props;
    return reduce(tableColumns, (columns, tableColumn) => {
      let column = null;
      if (tableColumn.tag === 'account') {
        column = this.getAccountColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityName') {
        column = this.getNameColumn(tableColumn);
      } else if (tableColumn.tag === 'stage') {
        column = this.getStageColumn(tableColumn);
      } else if (tableColumn.tag === 'amount') {
        column = this.getAmountColumn(tableColumn);
      } else if (tableColumn.tag === 'closeDate') {
        column = this.getCloseDateColumn(tableColumn);
      } else if (tableColumn.tag === 'age') {
        column = this.getAgeColumn(tableColumn);
      } else if (tableColumn.tag === 'products') {
        column = this.getProductColumn(tableColumn);
      } else if (tableColumn.tag === 'type') {
        column = this.getTypeColumn(tableColumn);
      } else if (tableColumn.tag === 'industries') {
        column = this.getIndustryColumn(tableColumn);
      } else if (tableColumn.tag === 'lastActivity') {
        column = this.getLastActivityColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityOwner' || (tableColumn.target_object === 'Opportunity' && tableColumn.target_field === 'owner_id')) {
        column = this.getOwnerColumn(tableColumn, false);
      } else if (tableColumn.tag === 'accountOwner' || (tableColumn.target_object === 'Account' && tableColumn.target_field === 'owner_id')) {
        column = this.getOwnerColumn(tableColumn, true);
      } else if (tableColumn.target_object === 'Opportunity' && tableColumn.target_field === 'requested_feedbacks') {
        column = this.getRequestedFeedbackColumn(tableColumn);
      } else if (tableColumn.tag === 'contactCount') {
        column = this.getContactCountColumn(tableColumn);
      } else {
        column = {
          column: tableColumn,
          customFieldType: CUSTOM_FIELD_TYPES.OPPORTUNITY,
        };
      }
      if (column) {
        columns.push(column);
      }
      return columns;
    }, []);
  }
  
  hanldeOnColumnRowGroupChanged(event) {
    const { columns } = event;
    const closeDateColumn = find(columns, col => col.colDef && col.colDef.cellRendererParams && col.colDef.cellRendererParams.isCloseDate);
    this.setState({ groupingByCloseDate: closeDateColumn !== undefined });
  }

  renderManagerFilter() {
    const { isManager } = this.props;
    const { showAllDeals } = this.state;
    if (!isManager) {
      return null;
    }
    return (
      <span>
        Show&nbsp;
        <Toggle
          onClick={() => {
            this.setState({ showAllDeals: !showAllDeals });
          }}
          on={<span>All Deals</span>}
          off={<span>My Deals</span>}
          size="xs"
          onstyle="success"
          active={showAllDeals}
        />
      </span>
    );
  }

  render() {
    const {
      opportunities, isManager, auth,
    } = this.props;
    const { showAllDeals } = this.state;

    if (opportunities.length === 0) {
      return (
        <div className="alert alert-danger">
          No Records Found.
        </div>
      );
    }
    let items = opportunities;
    if (isManager && !showAllDeals) {
      const userId = auth.user_id;
      items = filter(items, { owner_id: userId });
    }
    const columnDefs = this.getColDefs();
    return (
      <div>
        <GodTable
          id={this.props.tableName}
          columnDefs={columnDefs}
          rowData={items}
          onGridReady={this.onGridReady}
          frameworkComponents={{
            accountNameRenderer: AccountNameRenderer,
            opportunityNameRenderer: OpportunityNameRenderer,
            logRenderer: LogRenderer,
            ageRenderer: AgeRenderer,
            arrayRenderer: ArrayRenderer,
            dateRenderer: DateRenderer,
            saleRepsNameRenderer: SaleRepsNameRenderer,
          }}
          columnTypes={{
            dateColumn: {
              filter: 'agDateColumnFilter',
              filterParams: {
                comparator: dateComparator,
              },
            },
          }}
          onColumnRowGroupChanged={this.hanldeOnColumnRowGroupChanged}
          getMainMenuItems={this.getMainMenuItems}
          leftControls={this.renderManagerFilter()}
          enableFullScreen
          fullScreenMode={this.props.fullScreenMode}
          onExitFullScreen={this.props.onExitFullScreen}
          onEnterFullScreen={this.props.onEnterFullScreen}
          fullScreenOffset={this.props.fullScreenOffset}
          tableManageConfigs={{
            enableCustomMeta: true,
            validCustomMetaObjects: ['Opportunity'],
          }}
          resourceType={GodTable.RESOURCE_TYPE.OPPORTUNITY}
        />
      </div>
    );
  }
}

OpportunityTable.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({})),
  isManager: PropTypes.bool,
  auth: PropTypes.shape({}),
  tableColumns: PropTypes.arrayOf(PropTypes.shape({})),
  fullScreenMode: PropTypes.bool,
  onExitFullScreen: PropTypes.func,
  onEnterFullScreen: PropTypes.func,
  tableName: PropTypes.string.isRequired,
  fullScreenOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};
OpportunityTable.defaultProps = {
  opportunities: [],
  isManager: false,
  auth: null,
  tableColumns: [],
  fullScreenMode: false,
  onExitFullScreen: () => {},
  onEnterFullScreen: () => {},
  fullScreenOffset: {
    x: 0,
    y: 0,
  },
};
OpportunityTable.contextTypes = {
};


// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  return {
    auth: getUser(state),
    tableColumns: getTablePreference(state, ownProps.tableName),
  };
}

export default connect(mapStateToProps)(OpportunityTable);
